import axios from 'axios';
import { API_URL } from '../components/common/constants';


const PARKI_AUTH_REDIRECT_KEY = 'parkiAuthRedirectTo';
const PARKI_AUTH_REDIRECT_PARAM = 'parki_auth_redirect_to';

// Helper to set auth token on all requests
const setAuthToken = (token) => {
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete axios.defaults.headers.common['Authorization'];
    }
};

// Only allow redirecting back to parki.cloud and its subdomains to prevent open-redirect abuse.
const isValidParkiRedirectUrl = (url) => {
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
        const host = parsed.hostname.toLowerCase();
        return host === 'parki.cloud' || host.endsWith('.parki.cloud');
    } catch (err) {
        return false;
    }
};

// Capture parki_auth_redirect_to from the current URL (if present and valid) and persist
// it so we can redirect the user back after authentication completes.
const captureAuthRedirect = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const redirectTo = params.get(PARKI_AUTH_REDIRECT_PARAM);
        if (redirectTo && isValidParkiRedirectUrl(redirectTo)) {
            localStorage.setItem(PARKI_AUTH_REDIRECT_KEY, redirectTo);
        }
    } catch (err) {
        console.warn('Error capturing auth redirect:', err);
    }
};

const getPendingAuthRedirect = () => localStorage.getItem(PARKI_AUTH_REDIRECT_KEY);

const clearAuthRedirect = () => localStorage.removeItem(PARKI_AUTH_REDIRECT_KEY);

// If a valid pending external redirect exists, clear it and send the browser there.
// Returns true if a redirect was performed (caller should skip its own routing).
const consumePendingAuthRedirect = () => {
    const url = getPendingAuthRedirect();
    if (!url) return false;
    clearAuthRedirect();
    if (!isValidParkiRedirectUrl(url)) return false;
    window.location.replace(url);
    return true;
};

// Initialize auth from localStorage
const initAuth = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
        setAuthToken(token);
    }
};

// Login user
const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/login`, { email, password });
    if (response.data.status === 'success' && !response.data.passwordChangeRequired && !response.data.requiresMFA) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setAuthToken(response.data.token);
    }
    
    return response.data;
};

// Complete password change for new users or password reset
const completePasswordChange = async (session, newPassword, userAttributes = {}, requiredAttributes = []) => {
    try {
        const response = await axios.post(`${API_URL}/complete-password-change`, {
            session,
            newPassword,
            userAttributes,
            requiredAttributes
        });

        // If backend signals MFA (email OTP) is required, return challenge info
        if (
            response?.data?.success &&
            response?.data?.requiresMFA &&
            response?.data?.challengeName === 'CUSTOM_CHALLENGE'
        ) {
            return {
                success: true,
                requiresMFA: true,
                challengeName: response.data.challengeName,
                session: response.data.session,
                message: response.data.message
            };
        }

        // If successful and token/user are provided, persist them
        if (response.data.success) {
            if (response.data.token) {
                localStorage.setItem('authToken', response.data.token);
                setAuthToken(response.data.token);
            }
            if (response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
        }

        return response.data;
    } catch (error) {
        console.error('Error completing password change:', error);
        throw error;
    }
};

// Logout user
const logout = async () => {
    try {
        const token = localStorage.getItem('authToken');
        if (token) {
            // Call backend to invalidate the token
            await axios.post(`${API_URL}/logout`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
        }
    } catch (error) {
        console.warn('Error during logout API call:', error);
        // Continue with logout even if API call fails
    } finally {
        // Always clean up local storage and headers
        localStorage.removeItem('authToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedReport');
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('originalUser');
        setAuthToken(null);
    }
};

// Check if user is authenticated
const isAuthenticated = () => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
        return false;
    }
    
    try {
        // JWT tokens are split into three parts: header.payload.signature
        const payload = token.split('.')[1];
        
        // Decode the base64 payload
        const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        
        // Check if token has expired
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        
        if (decodedPayload.exp && decodedPayload.exp < currentTime) {
            // Token has expired, clean up
            localStorage.removeItem('authToken');
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            localStorage.removeItem('isImpersonating');
            localStorage.removeItem('originalUser');
            setAuthToken(null);
            return false;
        }
        
        return true;
    } catch (error) {
        // If there's any error parsing the token, assume it's invalid
        console.error('Error verifying token:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('originalUser');
        setAuthToken(null);
        return false;
    }
};

// Get current user
const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    var userData = {
      firstName: '',
      lastName: '',
      userType: '',
      entity: ''
    };

    if (userStr) {
      const user = JSON.parse(userStr);
      userData = {
        firstName: user.firstName || 'Unknown',
        lastName: user.lastName || '',
        userType: user.userType || 'customer',
        entity: user.entity || 'unknown'
      };
    }

    return userData;
};

const setCurrentUser = (user) => {
    localStorage.setItem('user', JSON.stringify(user));
};  

// Verify OTP code for MFA authentication
const verifyOTP = async (email, session, otpCode) => {
  try {
    
    const response = await axios.post(`${API_URL}/verify-otp`, {
      email: email,
      otp: otpCode,
      session: session
    }); 
    
    // If successful, store the authentication data
    if (response.data.success) {

      // Store user data if provided
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Store token if provided
      if (response.data.result && response.data.result.AccessToken) {
        localStorage.setItem('authToken', response.data.result.AccessToken);
        localStorage.setItem('idToken', response.data.result.IdToken);
        setAuthToken(response.data.result.AccessToken);
      }
    
    }
    
    return response.data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error.response?.data || error;
  }
};

// Request password reset (sends code to email)
const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(`${API_URL}/forgot-password`, { email });
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error.response?.data || error;
  }
};

// Confirm password reset with code
const confirmPasswordReset = async (email, code, newPassword) => {
  try {
    const response = await axios.post(`${API_URL}/reset-password`, {
      email,
      code,
      newPassword
    });
    
    // If MFA is required after password reset
    if (
      response.data.success &&
      response.data.requiresMFA &&
      response.data.challengeName === 'CUSTOM_CHALLENGE'
    ) {
      return {
        success: true,
        requiresMFA: true,
        challengeName: response.data.challengeName,
        session: response.data.session,
        message: response.data.message
      };
    }
    
    // If successful without MFA, store auth token
    if (response.data.success && response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuthToken(response.data.token);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error confirming password reset:', error);
    throw error.response?.data || error;
  }
};

// Demo login function
const loginDemo = async (username) => {
  try {
    const response = await axios.post(`${API_URL}/demo-login`, { username });
    
    if (response.data.success) {
      // Store auth data in localStorage, just like regular login
      localStorage.setItem('authToken', response.data.authToken);
      localStorage.setItem('idToken', response.data.idToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuthToken(response.data.authToken);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error logging in to demo:', error);
    throw error.response?.data || error;
  }
};



export default {
    setAuthToken,
    initAuth,
    login,
    logout,
    isAuthenticated,
    getCurrentUser,
    completePasswordChange,
    verifyOTP,
    requestPasswordReset,
    confirmPasswordReset,
    setCurrentUser,
    loginDemo,
    captureAuthRedirect,
    consumePendingAuthRedirect,
    clearAuthRedirect
};